import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import '@/components/ui/styles/customScrollBar.css';
import { Textarea } from "@/components/ui/textarea";
import { Upload, X } from 'lucide-react';
import React from 'react';

// Agency options for the dropdown
const AGENCY_OPTIONS = [
   { value: 'DPWH', label: 'DPWH - Department of Public Works and Highways' },
   { value: 'DOTR', label: 'DOTR - Department of Transportation' },
   { value: 'DEPED', label: 'DEPED - Department of Education' },
   { value: 'DA', label: 'DA - Department of Agriculture' },
   { value: 'DOH', label: 'DOH - Department of Health' },
   { value: 'CHED', label: 'CHED - Commission on Higher Education' },
   { value: 'DOE', label: 'DOE - Department of Energy' },
   { value: 'NIA', label: 'NIA - National Irrigation Administration' }
];

const TCEBPEditForm = ({ formData, handleInputChange, handleFileChange, removeFile }) => {
   const renderFileInputs = () => {
      const fileTypes = [
         { type: 'letterOfIntent', label: 'Letter of Intent', condition: formData.requestType === 'Inventory' },
         { type: 'applicationLetter', label: 'Application Letter', condition: formData.requestType !== 'Inventory' },
         { type: 'lguEndorsement', label: 'LGU Endorsement', condition: formData.requestType !== 'Inventory' },
         { type: 'landTenurial', label: 'Land Tenurial', condition: formData.requestType !== 'Inventory' && formData.documentRequirements?.isWithinTimberland },
         { type: 'siteDevelopmentPlan', label: 'Site Development Plan', condition: formData.requestType !== 'Inventory' },
         { type: 'environmentalCompliance', label: 'Environmental Compliance', condition: formData.requestType !== 'Inventory' },
         { type: 'fpic', label: 'FPIC', condition: formData.requestType !== 'Inventory' },
         { type: 'ownerConsent', label: 'Owner Consent', condition: formData.requestType !== 'Inventory' && formData.documentRequirements?.isTitledProperty },
         { type: 'pambClearance', label: 'PAMB Clearance', condition: formData.requestType !== 'Inventory' && formData.documentRequirements?.isWithinProtectedArea }
      ];

      return fileTypes.filter(fileType => fileType.condition).map(({ type, label }) => (
         <div key={type} className="mb-4">
            <Label htmlFor={type} className="block mb-2">
               {label}
            </Label>
            <div>
               {formData.files && formData.files[type] && formData.files[type].length > 0 ? (
                  formData.files[type].map((file, index) => (
                     <div key={index} className="flex items-center justify-between mb-2 bg-gray-100 p-2 rounded">
                        <span className="text-sm text-gray-600 truncate">{file.filename}</span>
                        <Button
                           type="button"
                           variant="ghost"
                           size="sm"
                           onClick={() => removeFile(type, index)}
                        >
                           <X className="h-4 w-4" />
                        </Button>
                     </div>
                  ))
               ) : (
                  <div className="mb-2 bg-gray-100 p-2 rounded">
                     <span className="text-sm text-gray-500">No uploaded file</span>
                  </div>
               )}
               <div className="flex items-center mt-2">
                  <Input
                     id={`${type}-${formData.files?.[type]?.length || 0}`}
                     name={type}
                     type="file"
                     onChange={(e) => handleFileChange(e, type)}
                     className="hidden"
                  />
                  <Label htmlFor={`${type}-${formData.files?.[type]?.length || 0}`} className="cursor-pointer flex items-center justify-center w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
                     <Upload className="mr-2 h-4 w-4" />
                     {formData.files && formData.files[type] && formData.files[type].length > 0
                        ? `Add Another ${label}`
                        : `Upload ${label}`
                     }
                  </Label>
               </div>
            </div>
         </div>
      ));
   };

   const renderDocumentRequirements = () => {
      if (formData.requestType === 'Inventory') return null;

      return (
         <div className="space-y-4 mb-6">
            <h3 className="text-lg font-semibold">Document Requirements</h3>
            <div className="space-y-2">
               <Checkbox
                  id="isWithinTimberland"
                  label="Is the property within timberland?"
                  checked={formData.documentRequirements?.isWithinTimberland || false}
                  onCheckedChange={(checked) => handleInputChange({
                     target: {
                        name: 'documentRequirements',
                        value: {
                           ...formData.documentRequirements,
                           isWithinTimberland: checked
                        }
                     }
                  })}
               />
               <Checkbox
                  id="isTitledProperty"
                  label="Is it a titled property?"
                  checked={formData.documentRequirements?.isTitledProperty || false}
                  onCheckedChange={(checked) => handleInputChange({
                     target: {
                        name: 'documentRequirements',
                        value: {
                           ...formData.documentRequirements,
                           isTitledProperty: checked
                        }
                     }
                  })}
               />
               <Checkbox
                  id="isWithinProtectedArea"
                  label="Is it within Protected Area?"
                  checked={formData.documentRequirements?.isWithinProtectedArea || false}
                  onCheckedChange={(checked) => handleInputChange({
                     target: {
                        name: 'documentRequirements',
                        value: {
                           ...formData.documentRequirements,
                           isWithinProtectedArea: checked
                        }
                     }
                  })}
               />
            </div>
         </div>
      );
   };

   return (
      <div className="pl-2 space-y-4 custom-scrollbar pr-4" style={{ maxHeight: 'calc(80vh - 200px)', overflowY: 'auto' }}>
         <div>
            <Label htmlFor="requestType">Request Type</Label>
            <RadioGroup
               name="requestType"
               value={formData.requestType}
               onValueChange={(value) => handleInputChange({
                  target: {
                     name: 'requestType',
                     value,
                     isRequestForCutting: value === 'Cutting',
                     isRequestForEarthBalling: value === 'Earth Balling'
                  }
               })}
            >
               <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Cutting" id="Cutting" />
                  <Label htmlFor="Cutting">Request for Cutting (NGA's)</Label>
               </div>
               <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Earth Balling" id="Earth Balling" />
                  <Label htmlFor="Earth Balling">Request for Earth Balling (NGA's)</Label>
               </div>
               <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Inventory" id="Inventory" />
                  <Label htmlFor="Inventory">Request for Inventory for Cutting/Earth Balling (NGA's)</Label>
               </div>
            </RadioGroup>
         </div>

         {formData.requestType === 'Inventory' && (
            <div className="ml-6 space-y-2 p-4 border-l-2 border-input">
               <Label className="text-sm font-medium mb-2">What is this inventory request for?</Label>
               <RadioGroup
                  name="inventoryType"
                  value={formData.isRequestForCutting ? 'Cutting' : formData.isRequestForEarthBalling ? 'Earth Balling' : ''}
                  onValueChange={(value) => {
                     handleInputChange({
                        target: {
                           name: 'formUpdate',
                           value: {
                              isRequestForCutting: value === 'Cutting',
                              isRequestForEarthBalling: value === 'Earth Balling'
                           }
                        }
                     });
                  }}
                  className="space-y-2"
               >
                  <div className="flex items-center space-x-2">
                     <RadioGroupItem value="Cutting" id="forCutting" />
                     <Label htmlFor="forCutting">For Tree Cutting</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                     <RadioGroupItem value="Earth Balling" id="forEarthBalling" />
                     <Label htmlFor="forEarthBalling">For Earth Balling</Label>
                  </div>
               </RadioGroup>
            </div>
         )}

         <div>
            <Label htmlFor="name">Name</Label>
            <Input
               id="name"
               name="name"
               value={formData.name || ''}
               onChange={handleInputChange}
               required
            />
         </div>
         <div>
            <Label htmlFor="projectLocation">Project Location (Barangay, Municipality, Province)</Label>
            <Input
               id="projectLocation"
               name="projectLocation"
               value={formData.projectLocation || ''}
               onChange={handleInputChange}
               placeholder="Enter project location"
               required
            />
         </div>
         <div className="space-y-2">
            <Label>Agency</Label>
            <div>
               <Label htmlFor="officeName">Office Name</Label>
               <select
                  id="officeName"
                  name="officeName"
                  value={formData.officeName || ''}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  required
               >
                  <option value="">Select Office</option>
                  {AGENCY_OPTIONS.map(option => (
                     <option key={option.value} value={option.value}>
                        {option.label}
                     </option>
                  ))}
               </select>
            </div>
         </div>
         <div>
            <Label htmlFor="officeAddress">Office Address</Label>
            <Input
               id="officeAddress"
               name="officeAddress"
               value={formData.officeAddress || ''}
               onChange={handleInputChange}
               placeholder="Enter office address"
               required
            />
         </div>
         <div>
            <Label htmlFor="contactNumber">Contact Number</Label>
            <Input
               id="contactNumber"
               name="contactNumber"
               value={formData.contactNumber || ''}
               onChange={handleInputChange}
               required
            />
         </div>
         <div>
            <Label htmlFor="purpose">Purpose</Label>
            <Textarea
               id="purpose"
               name="purpose"
               value={formData.purpose || ''}
               onChange={handleInputChange}
               required
            />
         </div>

         {formData.requestType !== 'Inventory' && (
            <div className="space-y-2">
               <div className="flex items-center space-x-2">
                  <Checkbox
                     id="isWithinTimberland"
                     checked={formData.documentRequirements?.isWithinTimberland || false}
                     onCheckedChange={(checked) => handleInputChange({
                        target: {
                           name: 'documentRequirements',
                           value: {
                              ...formData.documentRequirements,
                              isWithinTimberland: checked
                           }
                        }
                     })}
                  />
                  <Label htmlFor="isWithinTimberland">
                     Is the property within timberland?
                  </Label>
               </div>
               <div className="flex items-center space-x-2">
                  <Checkbox
                     id="isTitledProperty"
                     checked={formData.documentRequirements?.isTitledProperty || false}
                     onCheckedChange={(checked) => handleInputChange({
                        target: {
                           name: 'documentRequirements',
                           value: {
                              ...formData.documentRequirements,
                              isTitledProperty: checked
                           }
                        }
                     })}
                  />
                  <Label htmlFor="isTitledProperty">
                     Is it a titled property?
                  </Label>
               </div>
               <div className="flex items-center space-x-2">
                  <Checkbox
                     id="isWithinProtectedArea"
                     checked={formData.documentRequirements?.isWithinProtectedArea || false}
                     onCheckedChange={(checked) => handleInputChange({
                        target: {
                           name: 'documentRequirements',
                           value: {
                              ...formData.documentRequirements,
                              isWithinProtectedArea: checked
                           }
                        }
                     })}
                  />
                  <Label htmlFor="isWithinProtectedArea">
                     Is it within Protected Area?
                  </Label>
               </div>
            </div>
         )}

         <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-4">Documents</h3>
            {renderFileInputs()}
         </div>
      </div>
   );
};

export default TCEBPEditForm;
